import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

// Стиль для iOS-уведомления
const IOSNotification = styled(motion.div)`
  position: fixed;
  top: 8px;
  left: 0;
  right: 0;
  margin: 0 auto;
  background: rgba(240, 240, 240, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1002; /* Увеличиваем z-index чтобы было выше навбара */
  padding: 12px;
  width: 90%;
  max-width: 400px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: calc(100% - 32px); /* Больше отступы */
    max-width: none;
    top: 8px;
    left: 0;
    right: 0;
    margin: 0 auto;
  }
`;

const NotificationContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #007aff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TextContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContactName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #000;
  margin-bottom: 2px;
`;

const MainText = styled.div`
  font-size: 14px;
  color: #333;
  line-height: 1.3;
  margin-bottom: 8px;
`;

const LinksContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f0f0f0;
  border-radius: 12px;
  text-decoration: none;
  color: #007aff;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.2s ease;

  &:hover {
    background: #e0e0e0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

// Стиль для навигационной панели (хедера)
const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #ffffff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1001; /* Убедитесь что меньше чем у уведомления */
`;

// Стиль для логотипа
const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
`;

const LogoImage = styled.img`
  width: 30px;
  height: auto;
`;

const LogoText = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  color: #007aff;
  margin: 0;
`;

// Стиль для меню
const MenuButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const CloseButtonMenu = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  position: absolute;
  top: 1rem;
  right: 1rem;
`;

const MobileMenu = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 80%;
  max-width: 300px;
  background: white;
  box-shadow: -4px 0 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const MobileMenuItem = styled(Link)`
  display: block;
  padding: 1rem;
  width: 100%;
  text-align: center;
  text-decoration: none; /* Убираем подчеркивание */
  color: #007aff;
  font-size: 1.2rem;
  transition: background 0.3s ease;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

// Стиль для основного меню на ПК
const DesktopMenu = styled.ul`
  display: flex;
  gap: 1rem;
  list-style: none;
  margin-left: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Стиль для пунктов меню на ПК
const DesktopMenuItem = styled(Link)`
  display: block;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background 0.3s ease;
  text-decoration: none; /* Убираем подчеркивание */
  color: #007aff;
  outline: none;
  user-select: none;
  font-weight: ${({ isActive }) => (isActive ? "bold" : "normal")};

  &:hover {
    background-color: #f0f0f0;
  }
`;

// Анимация для выпадающего списка
const DropdownMenu = styled(motion.ul)`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  z-index: 10;
  padding: 0.5rem 0;
  list-style: none;
`;

const DropdownItem = styled(motion.li)`
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background-color: #f0f0f0;
  }

  a {
    text-decoration: none;
    color: #007aff;
    outline: none;
    user-select: none;
    font-weight: ${({ isActive }) => (isActive ? "bold" : "normal")};
  }
`;

// Стиль для блока с телефонами
const PhoneBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;

  @media (max-width: 768px) {
    display: none; /* Скрываем телефоны на мобильных устройствах */
  }
`;

const PhoneLink = styled.a`
  text-decoration: none;
  color: #007aff;
  font-size: 1rem;
  margin-bottom: 0.5rem;

  &:hover {
    text-decoration: none;
  }
`;

// Хук для получения текущего пути
const useActivePath = () => {
  const location = useLocation();
  return location.pathname;
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null); // Для отслеживания открытого выпадающего меню
  const [showNotification, setShowNotification] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const activePath = useActivePath();

  // Проверяем, показывали ли уже уведомление в этой сессии
  useEffect(() => {
    const notificationShown = sessionStorage.getItem('notificationShown');
    if (notificationShown) {
      setShowNotification(false);
    }
  }, []);

  const handleCloseNotification = () => {
    setShowNotification(false);
    sessionStorage.setItem('notificationShown', 'true');
  };

  const handleNotificationClick = () => {
    setExpanded(!expanded);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleDropdown = (index) => {
    if (dropdownOpen === index) {
      setDropdownOpen(null);
    } else {
      setDropdownOpen(index);
    }
  };

  useEffect(() => {
    if (menuOpen) {
      const handleRouteChange = () => setMenuOpen(false);
      window.addEventListener('popstate', handleRouteChange);
      return () => window.removeEventListener('popstate', handleRouteChange);
    }
  }, [menuOpen]);

  return (
    <>
      {showNotification && (
        <IOSNotification
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <NotificationContent onClick={handleNotificationClick}>
            <Avatar>
              <AvatarImage src="/logo_sonet_dom.gif" alt="Сонет-Дом" />
            </Avatar>
            <TextContainer>
              <ContactName>Сонет-Дом</ContactName>
              <MainText>
                Привет! Это абонентский отдел Сонет-Дом! Теперь ты можешь отправить заявку к нам в MAX или telegram
              </MainText>
              <LinksContainer
                initial={{ height: 0, opacity: 0 }}
                animate={{ 
                  height: expanded ? 'auto' : 0, 
                  opacity: expanded ? 1 : 0 
                }}
                transition={{ duration: 0.3 }}
              >
                <ContactLink href="https://max.ru/u/f9LHodD0cOKDEZ37obCGZx35sXKjiRk8WOeW5P1o2sLa96YzrE3A1MJlQ68" target="_blank">
                  📱 MAX Солнечногорск
                </ContactLink>
                <ContactLink href="https://t.me/sonet_dom_soln" target="_blank">
                  💬 Telegram Солнечногорск
                </ContactLink>
                <ContactLink href="https://max.ru/u/f9LHodD0cOJm7eGfYuwamt-APf0hXaVMLc-uGt9c6mG0rt8EfLpREY-TAtM" target="_blank">
                  📱 MAX Клин
                </ContactLink>
                <ContactLink href="https://t.me/sonet_dom_klin" target="_blank">
                  💬 Telegram Клин
                </ContactLink>
              </LinksContainer>
            </TextContainer>
            <CloseButton onClick={(e) => {
              e.stopPropagation();
              handleCloseNotification();
            }}>
              ×
            </CloseButton>
          </NotificationContent>
        </IOSNotification>
      )}

      <Nav>
        {/* Блок с логотипом и названием */}
        <LogoContainer to="/">
          <LogoImage src="/logo_sonet_dom.gif" alt="Логотип Сонет-Дом" />
          <LogoText>Сонет-Дом</LogoText>
        </LogoContainer>

        {/* Меню для ПК */}
        <DesktopMenu>
          {/* Главная */}
          <DesktopMenuItem to="/" isActive={activePath === "/"}>
            Главная
          </DesktopMenuItem>

          {/* Оборудование */}
          <DesktopMenuItem to="/equipment" isActive={activePath === "/equipment"}>
            Оборудование
          </DesktopMenuItem>

          {/* Тарифы */}
          <DesktopMenuItem to="/tariffs" isActive={activePath === "/tariffs"}>
            Тарифы
          </DesktopMenuItem>

          {/* Реквизиты */}
          <DesktopMenuItem to="/requisites" isActive={activePath === "/requisites"}>
            Реквизиты
          </DesktopMenuItem>

          {/* Контакты */}
          <DesktopMenuItem to="/contacts" isActive={activePath === "/contacts"}>
            Контакты
          </DesktopMenuItem>
        </DesktopMenu>

        {/* Кнопка меню для мобильной версии */}
        <MenuButton onClick={toggleMenu}>☰</MenuButton>

        {/* Мобильное меню */}
        {menuOpen && (
          <>
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={toggleMenu}
            />
            <MobileMenu
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3 }}
            >
              <CloseButtonMenu onClick={toggleMenu}>×</CloseButtonMenu>
              <MobileMenuItem to="/" onClick={toggleMenu}>
                Главная
              </MobileMenuItem>
              <MobileMenuItem to="/equipment" onClick={toggleMenu}>
                Оборудование
              </MobileMenuItem>
              <MobileMenuItem to="/tariffs" onClick={toggleMenu}>
                Тарифы
              </MobileMenuItem>
              <MobileMenuItem to="/requisites" onClick={toggleMenu}>
                Реквизиты
              </MobileMenuItem>
              <MobileMenuItem to="/contacts" onClick={toggleMenu}>
                Контакты
              </MobileMenuItem>
            </MobileMenu>
          </>
        )}

        {/* Блок с телефонами */}
        <PhoneBlock>
          <PhoneLink href="tel:+74959941398">г. Солнечногорск 8(495)994-13-98</PhoneLink>
          <PhoneLink href="tel:+74962437696">г. Клин 8(49624)3-76-96</PhoneLink>
        </PhoneBlock>
      </Nav>
    </>
  );
};

export default Header;